/**
 * Route Configuration
 *
 * Centralized route definitions for the application.
 * This provides type safety and makes route management easier.
 */

import React from "react";
import { Navigate } from "react-router-dom";
// ✅ CRITICAL: Import directly from source, not from @/contexts barrel
import type { UserRole } from "@/features/identity/contexts/AuthContext";

import { domainRoutes } from "./domain-routes";

function redirectTo(to: string) {
  return function DomainIndexRedirect() {
    return React.createElement(Navigate, { to, replace: true });
  };
}

function lazyNamed<T extends React.ComponentType<any>>(
  factory: () => Promise<any>,
  exportName: string,
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    const m = await factory();
    return {
      default: m[exportName] as T,
    };
  });
}

// ✅ CRITICAL: Only eagerly import LoginPage - it's the only route needed for login screen
// Signup and Unauthorized are lazy-loaded since they're not needed on initial load
import { LoginView } from "@/features/identity/views/LoginView";
const LoginPage = LoginView;
// ✅ LAZY LOADING: Signup and Unauthorized pages are lazy-loaded
// They're only loaded when user navigates to those routes
const SignupPage = lazyNamed(
  () => import("@/features/identity/views/SignupView"),
  "SignupView",
);
const UnauthorizedPage = lazyNamed(
  () => import("@/features/identity/views/UnauthorizedView"),
  "UnauthorizedView",
);
const ApiTesterPage = lazyNamed(
  () => import("@/features/dev/views/ApiTesterView"),
  "ApiTesterView",
);

// ✅ LAZY LOADING: All dashboard routes are lazy-loaded using React.lazy()
// This prevents Vite from analyzing and pre-bundling all domain code on login screen
// Routes are only loaded when actually navigated to (code-splitting)

// Identity domain
const IdentityDomainPage = lazyNamed(
  () => import("@/features/identity/views/IdentityDomainView"),
  "IdentityDomainView",
);
const HomePage = lazyNamed(
  () => import("@/features/dashboard/views/HomeView"),
  "HomeView",
);
const DashboardPage = lazyNamed(
  () => import("@/features/dashboard/views/DashboardView"),
  "DashboardView",
);
const OnboardingPage = lazyNamed(
  () => import("@/features/identity/views/OnboardingView"),
  "OnboardingView",
);

// Product domain (catalog bounded context)
const ProductsPage = lazyNamed(
  () => import("@/features/catalog/views/ProductsListView"),
  "ProductsListView",
);
const ProductDetailPage = lazyNamed(
  () => import("@/features/catalog/views/ProductDetailView"),
  "ProductDetailView",
);

// Content domain
const ContentLibraryPage = lazyNamed(
  () => import("@/features/content/views/ContentLibraryView"),
  "ContentLibraryView",
);
const ContentProjectDetailPage = lazyNamed(
  () => import("@/features/content/views/ContentProjectDetailView"),
  "ContentProjectDetailView",
);
const RenditionDetailPage = lazyNamed(
  () => import("@/features/content/views/RenditionDetailView"),
  "RenditionDetailView",
);
const FactoryListPage = lazyNamed(
  () => import("@/features/content/views/FactoryListView"),
  "FactoryListView",
);
const FactoryRunDetailPage = lazyNamed(
  () => import("@/features/content/views/FactoryRunDetailView"),
  "FactoryRunDetailView",
);
const FactoryProduceStudioPage = lazyNamed(
  () => import("@/features/content/views/FactoryProduceStudioView"),
  "FactoryProduceStudioView",
);
const FactoryReadyStudioPage = lazyNamed(
  () => import("@/features/content/views/FactoryReadyStudioView"),
  "FactoryReadyStudioView",
);
const HeroPage = lazyNamed(
  () => import("@/features/content/views/HeroView"),
  "HeroView",
);
const TemplatesPage = lazyNamed(
  () => import("@/features/content/views/TemplatesView"),
  "TemplatesView",
);
const VersionsPage = lazyNamed(
  () => import("@/features/content/views/VersionsView"),
  "VersionsView",
);
const HashtagsPage = lazyNamed(
  () => import("@/features/content/views/HashtagsView"),
  "HashtagsView",
);

// Social domain
const ChannelsHubPage = lazyNamed(
  () => import("@/features/social/views/ChannelsHubView"),
  "ChannelsHubView",
);
const PublishLedgerPage = lazyNamed(
  () => import("@/features/social/views/PublishLedgerView"),
  "PublishLedgerView",
);
const ChannelDetailPage = lazyNamed(
  () => import("@/features/social/views/ChannelDetailView"),
  "ChannelDetailView",
);
const ChannelComposerPage = lazyNamed(
  () => import("@/features/social/views/ChannelComposerView"),
  "ChannelComposerView",
);
const ChannelSetupPage = lazyNamed(
  () => import("@/features/social/views/ConnectionsHubView"),
  "ConnectionsHubView",
);
const ProviderSetupPage = lazyNamed(
  () => import("@/features/social/views/ConnectionsHubView"),
  "ConnectionsHubView",
);
const ProvidersPage = lazyNamed(
  () => import("@/features/social/views/ConnectionsHubView"),
  "ConnectionsHubView",
);
const ProviderConnectPage = lazyNamed(
  () => import("@/features/social/views/ProviderConnectCallbackView"),
  "ProviderConnectCallbackView",
);

// Publishing domain
const PublishingQueuePage = lazyNamed(
  () => import("@/features/publishing/views/PublishingQueueView"),
  "PublishingQueueView",
);
const PublishingCalendarPage = lazyNamed(
  () => import("@/features/publishing/views/PublishingCalendarView"),
  "PublishingCalendarView",
);
const PublishingApprovalsPage = lazyNamed(
  () => import("@/features/publishing/views/PublishingApprovalsView"),
  "PublishingApprovalsView",
);
const PublishingReleasesPage = lazyNamed(
  () => import("@/features/publishing/views/PublishingReleasesView"),
  "PublishingReleasesView",
);
const PublicationDetailPage = lazyNamed(
  () => import("@/features/publishing/views/PublicationDetailView"),
  "PublicationDetailView",
);
const CreatePublicationWizardPage = lazyNamed(
  () => import("@/features/publishing/views/CreatePublicationWizardView"),
  "CreatePublicationWizardView",
);
const QuickPublishPage = lazyNamed(
  () => import("@/features/publishing/views/QuickPublishView"),
  "QuickPublishView",
);
const DeliveryManifestDetailPage = lazyNamed(
  () => import("@/features/publishing/views/DeliveryManifestDetailView"),
  "DeliveryManifestDetailView",
);

// Channels domain (thin wrappers over social / content surfaces)
const DestinationsPage = lazyNamed(
  () => import("@/features/channels/views/DestinationsView"),
  "DestinationsView",
);
const DeliveriesPage = lazyNamed(
  () => import("@/features/channels/views/DeliveriesView"),
  "DeliveriesView",
);
const PublishedContentPage = lazyNamed(
  () => import("@/features/channels/views/PublishedContentView"),
  "PublishedContentView",
);
const ConnectionHealthPage = lazyNamed(
  () => import("@/features/channels/views/ConnectionHealthView"),
  "ConnectionHealthView",
);
// Conversation domain
const ConversationDomainPage = lazyNamed(
  () => import("@/features/conversation/views/ConversationDomainView"),
  "ConversationDomainView",
);
const InboxPage = lazyNamed(
  () => import("@/features/conversation/views/InboxView"),
  "InboxView",
);
const InboxHubPage = lazyNamed(
  () => import("@/features/conversation/views/InboxHubView"),
  "InboxHubView",
);

// Planning domain
const PlanningDomainPage = lazyNamed(
  () => import("@/features/planning/views/PlanningDomainView"),
  "PlanningDomainView",
);
const SchedulingPage = lazyNamed(
  () => import("@/features/planning/views/SchedulingView"),
  "SchedulingView",
);
const CalendarPage = lazyNamed(
  () => import("@/features/planning/views/CalendarView"),
  "CalendarView",
);
const ConflictDetailPage = lazyNamed(
  () => import("@/features/planning/views/ConflictDetailView"),
  "ConflictDetailView",
);

// AI domain
const AIDomainPage = lazyNamed(
  () => import("@/features/ai/views/AIDomainView"),
  "AIDomainView",
);
const AIStudioPage = lazyNamed(
  () => import("@/features/ai/views/AIStudioView"),
  "AIStudioView",
);
const AIAssistantPage = lazyNamed(
  () => import("@/features/ai/views/AIAssistantView"),
  "AIAssistantView",
);
const AddModelPage = lazyNamed(
  () => import("@/features/ai/views/AddModelView"),
  "AddModelView",
);
const ConfigureModelPage = lazyNamed(
  () => import("@/features/ai/views/ConfigureModelView"),
  "ConfigureModelView",
);

// Metrics domain
const MetricsDomainPage = lazyNamed(
  () => import("@/features/metrics/views/MetricsDomainView"),
  "MetricsDomainView",
);
const ExperimentsPage = lazyNamed(
  () => import("@/features/metrics/views/ExperimentsView"),
  "ExperimentsView",
);
const ActivityTimelinePage = lazyNamed(
  () => import("@/features/activity/views/ActivityTimelineView"),
  "ActivityTimelineView",
);
const CampaignAnalyticsPage = lazyNamed(
  () => import("@/features/metrics/views/CampaignAnalyticsView"),
  "CampaignAnalyticsView",
);

// Insights domain (Meta Business Suite Insights)
const InsightsOverviewPage = lazyNamed(
  () => import("@/features/insights/views/InsightsOverviewView"),
  "InsightsOverviewView",
);
const InsightsPlanPage = lazyNamed(
  () => import("@/features/insights/views/InsightsPlanView"),
  "InsightsPlanView",
);
const InsightsResultsPage = lazyNamed(
  () => import("@/features/insights/views/InsightsResultsView"),
  "InsightsResultsView",
);
const InsightsAudiencePage = lazyNamed(
  () => import("@/features/insights/views/InsightsAudienceView"),
  "InsightsAudienceView",
);
const InsightsLeadsPage = lazyNamed(
  () => import("@/features/insights/views/InsightsLeadsView"),
  "InsightsLeadsView",
);
const InsightsMessagingPage = lazyNamed(
  () => import("@/features/insights/views/InsightsMessagingView"),
  "InsightsMessagingView",
);
const InsightsBenchmarkingPage = lazyNamed(
  () => import("@/features/insights/views/InsightsBenchmarkingView"),
  "InsightsBenchmarkingView",
);
const InsightsContentOverviewPage = lazyNamed(
  () => import("@/features/insights/views/InsightsContentOverviewView"),
  "InsightsContentOverviewView",
);
const InsightsContentLibraryPage = lazyNamed(
  () => import("@/features/insights/views/InsightsContentLibraryView"),
  "InsightsContentLibraryView",
);
const InsightsContentAdsPage = lazyNamed(
  () => import("@/features/insights/views/InsightsContentAdsView"),
  "InsightsContentAdsView",
);
const InsightsEarningsPage = lazyNamed(
  () => import("@/features/insights/views/InsightsEarningsView"),
  "InsightsEarningsView",
);

// Marketing domain
const MarketingDomainPage = lazyNamed(
  () => import("@/features/marketing/views/MarketingDomainView"),
  "MarketingDomainView",
);
const MarketingCampaignsPage = lazyNamed(
  () => import("@/features/marketing/views/CampaignsListView"),
  "CampaignsListView",
);
const CampaignDetailPage = lazyNamed(
  () => import("@/features/marketing/views/CampaignDetailView"),
  "CampaignDetailView",
);
const ContentAssetsPage = lazyNamed(
  () => import("@/features/content/views/ContentAssetsView"),
  "ContentAssetsView",
);
const ContentAssetDetailPage = lazyNamed(
  () => import("@/features/content/views/ContentAssetDetailView"),
  "ContentAssetDetailView",
);

// Sales domain
const SalesDomainPage = lazyNamed(
  () => import("@/features/sales/views/SalesDomainView"),
  "SalesDomainView",
);
const SalesListsPage = lazyNamed(
  () => import("@/features/sales/views/SalesListsView"),
  "SalesListsView",
);
const SalesListDetailPage = lazyNamed(
  () => import("@/features/sales/views/SalesListDetailView"),
  "SalesListDetailView",
);

// Entitlement domain
const EntitlementDomainPage = lazyNamed(
  () => import("@/features/entitlement/views/EntitlementDomainView"),
  "EntitlementDomainView",
);
const BillingPage = lazyNamed(
  () => import("@/features/entitlement/views/BillingView"),
  "BillingView",
);

// Compliance domain
const CompliancePage = lazyNamed(
  () => import("@/features/compliance/views/ComplianceView"),
  "ComplianceView",
);

// Observability domain
const ObservabilityPage = lazyNamed(
  () => import("@/features/operation/views/ObservabilityView"),
  "ObservabilityView",
);

// Operation domain
const OperationDomainPage = lazyNamed(
  () => import("@/features/operation/views/OperationDomainView"),
  "OperationDomainView",
);
const SupportPage = lazyNamed(
  () => import("@/features/operation/views/SupportView"),
  "SupportView",
);
const PlatformAdminPage = lazyNamed(
  () =>
    import("@/features/operation/views/platform-admin/PlatformAdminIndexView"),
  "PlatformAdminIndexView",
);
const PlatformAdminOrgsPage = lazyNamed(
  () =>
    import("@/features/operation/views/platform-admin/PlatformAdminOrgsView"),
  "PlatformAdminOrgsView",
);
const PlatformAdminOrgDetailPage = lazyNamed(
  () =>
    import("@/features/operation/views/platform-admin/PlatformAdminOrgDetailView"),
  "PlatformAdminOrgDetailView",
);
const PlatformAdminUsersPage = lazyNamed(
  () =>
    import("@/features/operation/views/platform-admin/PlatformAdminUsersView"),
  "PlatformAdminUsersView",
);
const PlatformAdminUserDetailPage = lazyNamed(
  () =>
    import("@/features/operation/views/platform-admin/PlatformAdminUserDetailView"),
  "PlatformAdminUserDetailView",
);
const PlatformAdminPlansPage = lazyNamed(
  () =>
    import("@/features/operation/views/platform-admin/PlatformAdminPlansView"),
  "PlatformAdminPlansView",
);
const PlatformAdminHealthPage = lazyNamed(
  () =>
    import("@/features/operation/views/platform-admin/PlatformAdminHealthView"),
  "PlatformAdminHealthView",
);
const PlatformAdminSettingsPage = lazyNamed(
  () =>
    import("@/features/operation/views/platform-admin/PlatformAdminSettingsView"),
  "PlatformAdminSettingsView",
);
const PlatformAdminAuditLogsPage = lazyNamed(
  () =>
    import("@/features/operation/views/platform-admin/PlatformAdminAuditLogsView"),
  "PlatformAdminAuditLogsView",
);
const PlatformAdminBaseTablePage = lazyNamed(
  () =>
    import("@/features/operation/views/platform-admin/PlatformAdminBaseTableView"),
  "PlatformAdminBaseTableView",
);
const PlatformAdminTemplatesPage = lazyNamed(
  () =>
    import("@/features/operation/views/platform-admin/PlatformAdminTemplatesView"),
  "PlatformAdminTemplatesView",
);
const OrgAdminPage = lazyNamed(
  () => import("@/features/operation/views/org-admin/OrgAdminIndexView"),
  "OrgAdminIndexView",
);
const OrgAdminTeamMembersPage = lazyNamed(
  () => import("@/features/operation/views/org-admin/OrgAdminTeamMembersView"),
  "OrgAdminTeamMembersView",
);
const OrgAdminSettingsPage = lazyNamed(
  () => import("@/features/operation/views/org-admin/OrgAdminSettingsView"),
  "OrgAdminSettingsView",
);
const OrgAdminAnalyticsPage = lazyNamed(
  () => import("@/features/operation/views/org-admin/OrgAdminAnalyticsView"),
  "OrgAdminAnalyticsView",
);
const OrgAdminSecurityPage = lazyNamed(
  () => import("@/features/operation/views/org-admin/OrgAdminSecurityView"),
  "OrgAdminSecurityView",
);
const OrgAiContextPage = lazyNamed(
  () => import("@/features/operation/views/org-admin/OrgAiContextView"),
  "OrgAiContextView",
);

// Cross-domain
const CrossDomainPage = lazyNamed(
  () => import("@/features/cross-domain/views/CrossDomainView"),
  "CrossDomainView",
);
const SearchPage = lazyNamed(
  () => import("@/features/cross-domain/views/SearchView"),
  "SearchView",
);
const HelpPage = lazyNamed(
  () => import("@/features/cross-domain/views/HelpView"),
  "HelpView",
);
const SettingsPage = lazyNamed(
  () => import("@/features/cross-domain/views/settings/SettingsView"),
  "SettingsView",
);
const ProviderSetupSettingsPage = lazyNamed(
  () => import("@/features/cross-domain/views/settings/ProviderSetupView"),
  "ProviderSetupView",
);
const ProviderSetupDetailPage = lazyNamed(
  () =>
    import("@/features/cross-domain/views/settings/ProviderSetupDetailView"),
  "ProviderSetupDetailView",
);
const AIProvidersSetupPage = lazyNamed(
  () => import("@/features/cross-domain/views/settings/AIProvidersSetupView"),
  "AIProvidersSetupView",
);
const PersonalSettingsPage = lazyNamed(
  () => import("@/features/cross-domain/views/settings/PersonalSettingsView"),
  "PersonalSettingsView",
);
const NotificationPreferencesPage = lazyNamed(
  () =>
    import("@/features/cross-domain/views/settings/NotificationPreferencesView"),
  "NotificationPreferencesView",
);
const SecuritySettingsPage = lazyNamed(
  () => import("@/features/cross-domain/views/settings/SecuritySettingsView"),
  "SecuritySettingsView",
);
const SocialChannelsAvailabilityPage = lazyNamed(
  () =>
    import(
      "@/features/cross-domain/views/settings/SocialChannelsAvailabilityView"
    ),
  "SocialChannelsAvailabilityView",
);

// Engagement domain
const EngagementInteractionsPage = lazyNamed(
  () => import("@/features/engagement/views/EngagementInteractionsView"),
  "EngagementInteractionsView",
);
const EngagementInteractionDetailPage = lazyNamed(
  () => import("@/features/engagement/views/EngagementInteractionDetailView"),
  "EngagementInteractionDetailView",
);
const EngagementInteractionNewPage = lazyNamed(
  () => import("@/features/engagement/views/EngagementInteractionNewView"),
  "EngagementInteractionNewView",
);

// Pulse domain
const PulseSignalsPage = lazyNamed(
  () => import("@/features/pulse/views/PulseSignalsView"),
  "PulseSignalsView",
);
const PulseSignalDetailPage = lazyNamed(
  () => import("@/features/pulse/views/PulseSignalDetailView"),
  "PulseSignalDetailView",
);
const PulseTrendsPage = lazyNamed(
  () => import("@/features/pulse/views/PulseTrendsView"),
  "PulseTrendsView",
);
const PulseTrendDetailPage = lazyNamed(
  () => import("@/features/pulse/views/PulseTrendDetailView"),
  "PulseTrendDetailView",
);
const PulseSourcesPage = lazyNamed(
  () => import("@/features/pulse/views/PulseSourcesView"),
  "PulseSourcesView",
);
const PulseSourceDetailPage = lazyNamed(
  () => import("@/features/pulse/views/PulseSourceDetailView"),
  "PulseSourceDetailView",
);
const PulseSourceNewPage = lazyNamed(
  () => import("@/features/pulse/views/PulseSourceNewView"),
  "PulseSourceNewView",
);
const PulseAlertsPage = lazyNamed(
  () => import("@/features/pulse/views/PulseAlertsView"),
  "PulseAlertsView",
);
const PulseAlertDetailPage = lazyNamed(
  () => import("@/features/pulse/views/PulseAlertDetailView"),
  "PulseAlertDetailView",
);
const PulseAlertNewPage = lazyNamed(
  () => import("@/features/pulse/views/PulseAlertNewView"),
  "PulseAlertNewView",
);

// Knowledge domain
const TaxonomiesListPage = lazyNamed(
  () => import("@/features/knowledge/views/TaxonomiesListView"),
  "TaxonomiesListView",
);
const TaxonomyDetailPage = lazyNamed(
  () => import("@/features/knowledge/views/TaxonomyDetailView"),
  "TaxonomyDetailView",
);
const KnowledgeTagsPage = lazyNamed(
  () => import("@/features/knowledge/views/KnowledgeTagsView"),
  "KnowledgeTagsView",
);
const KnowledgeDiscoverPage = lazyNamed(
  () => import("@/features/knowledge/views/KnowledgeDiscoverView"),
  "KnowledgeDiscoverView",
);

// Notification admin
const NotificationAdminPage = lazyNamed(
  () => import("@/features/notification/views/NotificationAdminView"),
  "NotificationAdminView",
);

// Operation boards
const IncidentsPage = lazyNamed(
  () => import("@/features/operation/views/IncidentsView"),
  "IncidentsView",
);
const TasksPage = lazyNamed(
  () => import("@/features/operation/views/TasksView"),
  "TasksView",
);
const EscalationsPage = lazyNamed(
  () => import("@/features/operation/views/EscalationsView"),
  "EscalationsView",
);

// Activity notifications
const ActivityNotificationsPage = lazyNamed(
  () => import("@/features/activity/views/ActivityNotificationsView"),
  "ActivityNotificationsView",
);

// Planning / Metrics / Sales depth
const PlanningExecutionsPage = lazyNamed(
  () => import("@/features/planning/views/ExecutionsView"),
  "ExecutionsView",
);
const MetricsDashboardsPage = lazyNamed(
  () => import("@/features/metrics/views/DashboardsView"),
  "DashboardsView",
);
const SalesSequencesPage = lazyNamed(
  () => import("@/features/sales/views/SalesSequencesView"),
  "SalesSequencesView",
);

export interface RouteConfig {
  path: string;
  component: React.ComponentType;
  layout?: "dashboard" | "auth" | "settings" | "platform-admin";
  protected?: boolean;
  requiredRole?: UserRole;
  metadata?: {
    title?: string;
    description?: string;
  };
}

/**
 * Public routes (no authentication required)
 */
export const publicRoutes: RouteConfig[] = [
  {
    path: "/login",
    component: LoginPage,
    layout: "auth",
    protected: false,
    metadata: { title: "Login" },
  },
  {
    path: "/signup",
    component: SignupPage,
    layout: "auth",
    protected: false,
    metadata: { title: "Sign Up" },
  },
  {
    path: "/unauthorized",
    component: UnauthorizedPage,
    layout: "auth",
    protected: false,
    metadata: { title: "Unauthorized" },
  },
  {
    path: "/tools/api-tester",
    component: ApiTesterPage,
    layout: "auth",
    protected: false,
    metadata: { title: "API Tester" },
  },
];

/**
 * Protected dashboard routes (authentication required)
 */
export const dashboardRoutes: RouteConfig[] = [
  // Base domain routes
  {
    path: "identity",
    component: IdentityDomainPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Identity" },
  },
  {
    path: "home",
    component: HomePage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Home" },
  },
  {
    path: "dashboard",
    component: DashboardPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Dashboard" },
  },
  {
    path: "catalog/products",
    component: ProductsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Products" },
  },
  {
    path: "content/projects",
    component: ContentLibraryPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Content Projects" },
  },
  {
    path: "content/projects/:contentProjectId/renditions/:renditionId",
    component: RenditionDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Rendition" },
  },
  {
    path: "content/projects/:contentProjectId",
    component: ContentProjectDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Content Project" },
  },
  {
    path: "content/production",
    component: FactoryListPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Production" },
  },
  {
    path: "content/production/new",
    component: FactoryListPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "New production run" },
  },
  {
    path: "content/production/:factoryRunId",
    component: FactoryRunDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Production run" },
  },
  {
    path: "content/production/:factoryRunId/produce/:formatType",
    component: FactoryProduceStudioPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Produce studio" },
  },
  {
    path: "content/production/:factoryRunId/ready/:formatType",
    component: FactoryReadyStudioPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Ready studio" },
  },
  {
    path: "content/taxonomy",
    component: HashtagsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Taxonomy" },
  },
  {
    path: "conversation",
    component: ConversationDomainPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Conversation" },
  },
  {
    path: "planning",
    component: PlanningDomainPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Planning" },
  },
  {
    path: "ai",
    component: AIDomainPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "AI" },
  },
  {
    path: "metrics",
    component: MetricsDomainPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Metrics" },
  },
  {
    path: "marketing",
    component: MarketingDomainPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Marketing" },
  },
  {
    path: "sales",
    component: SalesDomainPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Sales" },
  },
  {
    path: "entitlement",
    component: EntitlementDomainPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Entitlement" },
  },
  {
    path: "compliance",
    component: CompliancePage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Compliance" },
  },
  {
    path: "observability",
    component: ObservabilityPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Observability" },
  },
  {
    path: "operation",
    component: OperationDomainPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Operation" },
  },
  {
    path: "cross-domain",
    component: CrossDomainPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Cross Domain" },
  },
  // Specific sub-routes
  {
    path: "catalog/products/:id",
    component: ProductDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Product Detail" },
  },
  {
    path: "social/connections/callback",
    component: ProviderConnectPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Connect Provider" },
  },
  {
    path: "channels/destinations",
    component: DestinationsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Destinations" },
  },
  {
    path: "channels/destinations/:channelId",
    component: ChannelDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Destination" },
  },
  {
    path: "channels/deliveries",
    component: DeliveriesPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Deliveries" },
  },
  {
    path: "channels/published",
    component: PublishedContentPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Published Content" },
  },
  {
    path: "channels/health",
    component: ConnectionHealthPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Connection Health" },
  },
  {
    path: "channels/health/callback",
    component: ProviderConnectPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Connect Provider" },
  },
  {
    path: "publishing/queue",
    component: PublishingQueuePage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Publishing Queue" },
  },
  {
    path: "publishing/calendar",
    component: PublishingCalendarPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Publishing Calendar" },
  },
  {
    path: "publishing/approvals",
    component: PublishingApprovalsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Publishing Approvals" },
  },
  {
    path: "publishing/releases",
    component: PublishingReleasesPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Publishing Releases" },
  },
  {
    path: "publishing/publications/new",
    component: CreatePublicationWizardPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Create Publication" },
  },
  {
    path: "publishing/publications/:publicationId",
    component: PublicationDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Publication" },
  },
  {
    path: "publishing/manifests/:deliveryManifestId",
    component: DeliveryManifestDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Delivery Manifest" },
  },
  {
    path: "publishing/quick",
    component: QuickPublishPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Quick Publish" },
  },
  {
    path: "operation/escalations",
    component: EscalationsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Escalations" },
  },
  {
    path: "inbox",
    component: InboxHubPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Inbox" },
  },
  {
    path: "conversation/threads",
    component: InboxPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Conversations" },
  },
  {
    path: "planning/schedules",
    component: SchedulingPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Scheduling" },
  },
  {
    path: "planning/conflicts",
    component: redirectTo(domainRoutes.planning.calendar),
    layout: "dashboard",
    protected: true,
    metadata: { title: "Conflicts" },
  },
  {
    path: "planning/conflicts/:conflictId",
    component: ConflictDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Conflict Details" },
  },
  {
    path: "ai/assistant",
    component: AIAssistantPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "AI Assistant" },
  },
  {
    path: "ai/studio",
    component: AIStudioPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "AI Studio" },
  },
  {
    path: "ai/models/new",
    component: AddModelPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Add AI Model" },
  },
  {
    path: "ai/models/:modelTypeId/configure",
    component: ConfigureModelPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Configure AI Model" },
  },
  {
    path: "metrics/experiments",
    component: ExperimentsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Experiments" },
  },
  {
    path: "activity/events",
    component: ActivityTimelinePage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Activity Timeline" },
  },
  {
    path: "activity/notifications",
    component: ActivityNotificationsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Notifications" },
  },
  {
    path: "engagement",
    component: EngagementInteractionsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Engage" },
  },
  {
    path: "engagement/interactions",
    component: EngagementInteractionsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Engage" },
  },
  {
    path: "engagement/interactions/new",
    component: EngagementInteractionNewPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "New Interaction" },
  },
  {
    path: "engagement/interactions/:interactionId",
    component: EngagementInteractionDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Interaction Detail" },
  },
  {
    path: "pulse",
    component: PulseSignalsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Market Pulse" },
  },
  {
    path: "pulse/signals",
    component: PulseSignalsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Signals" },
  },
  {
    path: "pulse/signals/:signalId",
    component: PulseSignalDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Signal Detail" },
  },
  {
    path: "pulse/trends",
    component: PulseTrendsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Trends" },
  },
  {
    path: "pulse/trends/:trendId",
    component: PulseTrendDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Trend Detail" },
  },
  {
    path: "pulse/sources",
    component: PulseSourcesPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Pulse Sources" },
  },
  {
    path: "pulse/sources/new",
    component: PulseSourceNewPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "New Pulse Source" },
  },
  {
    path: "pulse/sources/:sourceId",
    component: PulseSourceDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Pulse Source" },
  },
  {
    path: "pulse/alerts",
    component: PulseAlertsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Pulse Alerts" },
  },
  {
    path: "pulse/alerts/new",
    component: PulseAlertNewPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "New Pulse Alert" },
  },
  {
    path: "pulse/alerts/:alertId",
    component: PulseAlertDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Pulse Alert" },
  },
  {
    path: "knowledge",
    component: TaxonomiesListPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Knowledge" },
  },
  {
    path: "knowledge/taxonomies",
    component: TaxonomiesListPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Taxonomies" },
  },
  {
    path: "knowledge/taxonomies/:taxonomyId",
    component: TaxonomyDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Taxonomy" },
  },
  {
    path: "knowledge/tags",
    component: KnowledgeTagsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Knowledge Tags" },
  },
  {
    path: "knowledge/discover",
    component: KnowledgeDiscoverPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Discover" },
  },
  {
    path: "notification",
    component: NotificationAdminPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Notification Admin" },
  },
  {
    path: "notification/templates",
    component: NotificationAdminPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Notification templates" },
  },
  {
    path: "notification/messages",
    component: NotificationAdminPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Notification Messages" },
  },
  {
    path: "notification/deliveries",
    component: NotificationAdminPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Notification Deliveries" },
  },
  {
    path: "operation/incidents",
    component: IncidentsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Incidents" },
  },
  {
    path: "operation/tasks",
    component: TasksPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Tasks" },
  },
  {
    path: "planning/executions",
    component: PlanningExecutionsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Executions" },
  },
  {
    path: "metrics/dashboards",
    component: MetricsDashboardsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Dashboards" },
  },
  {
    path: "sales/sequences",
    component: SalesSequencesPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Sales Sequences" },
  },
  {
    path: "metrics/campaigns",
    component: CampaignAnalyticsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Campaign Analytics" },
  },
  {
    path: "operation/tickets",
    component: SupportPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Support" },
  },
  {
    path: "entitlement/subscriptions",
    component: BillingPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Billing" },
  },
  {
    path: "operation/org-admin",
    component: OrgAdminPage,
    layout: "dashboard",
    protected: true,
    requiredRole: "org_admin",
    metadata: { title: "Organization Administration" },
  },
  {
    path: "operation/org-admin/team-members",
    component: OrgAdminTeamMembersPage,
    layout: "dashboard",
    protected: true,
    requiredRole: "org_admin",
    metadata: { title: "Team Members" },
  },
  {
    path: "operation/org-admin/settings",
    component: OrgAdminSettingsPage,
    layout: "dashboard",
    protected: true,
    requiredRole: "org_admin",
    metadata: { title: "Organization Settings" },
  },
  {
    path: "operation/org-admin/ai-context",
    component: OrgAiContextPage,
    layout: "dashboard",
    protected: true,
    requiredRole: "org_admin",
    metadata: { title: "AI Context" },
  },
  {
    path: "operation/org-admin/ai-content",
    component: OrgAiContextPage,
    layout: "dashboard",
    protected: true,
    requiredRole: "org_admin",
    metadata: { title: "AI Context" },
  },
  {
    path: "operation/org-admin/analytics",
    component: OrgAdminAnalyticsPage,
    layout: "dashboard",
    protected: true,
    requiredRole: "org_admin",
    metadata: { title: "Usage & Analytics" },
  },
  {
    path: "operation/org-admin/security",
    component: OrgAdminSecurityPage,
    layout: "dashboard",
    protected: true,
    requiredRole: "org_admin",
    metadata: { title: "Security & Compliance" },
  },
  {
    path: "planning/calendar",
    component: CalendarPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Calendar" },
  },
  {
    path: "insights",
    component: redirectTo(domainRoutes.insights.overview),
    layout: "dashboard",
    protected: true,
    metadata: { title: "Insights" },
  },
  {
    path: "insights/overview",
    component: InsightsOverviewPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Insights · Overview" },
  },
  {
    path: "insights/plan",
    component: InsightsPlanPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Insights · Plan" },
  },
  {
    path: "insights/results",
    component: InsightsResultsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Insights · Results" },
  },
  {
    path: "insights/audience",
    component: InsightsAudiencePage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Insights · Audience" },
  },
  {
    path: "insights/leads",
    component: InsightsLeadsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Insights · Leads" },
  },
  {
    path: "insights/messaging",
    component: InsightsMessagingPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Insights · Messaging" },
  },
  {
    path: "insights/benchmarking",
    component: InsightsBenchmarkingPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Insights · Benchmarking" },
  },
  {
    path: "insights/content",
    component: InsightsContentOverviewPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Insights · Content" },
  },
  {
    path: "insights/content/library",
    component: InsightsContentLibraryPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Insights · Content library" },
  },
  {
    path: "insights/content/ads",
    component: InsightsContentAdsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Insights · Ads" },
  },
  {
    path: "insights/content/earnings",
    component: InsightsEarningsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Insights · Earnings" },
  },
  {
    path: "content/tags",
    component: HashtagsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Hashtags" },
  },
  {
    path: "help",
    component: HelpPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Help" },
  },
  {
    path: "content/hero",
    component: HeroPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Hero Assets" },
  },
  {
    path: "identity/onboarding",
    component: OnboardingPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Onboarding" },
  },
  {
    path: "search",
    component: SearchPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Search" },
  },
  {
    path: "content/templates",
    component: TemplatesPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Templates" },
  },
  {
    path: "content/versions",
    component: VersionsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Versions" },
  },
  {
    path: "connectivity/providers",
    component: ProvidersPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Providers" },
  },
  {
    path: "marketing/campaigns",
    component: MarketingCampaignsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Marketing Campaigns" },
  },
  {
    path: "marketing/campaigns/:id",
    component: CampaignDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Campaign Detail" },
  },
  {
    path: "content/assets",
    component: ContentAssetsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Content Assets" },
  },
  {
    path: "content/assets/:assetId",
    component: ContentAssetDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "Asset Detail" },
  },
  {
    path: "sales/lists",
    component: SalesListsPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "External Lists" },
  },
  {
    path: "sales/lists/:id",
    component: SalesListDetailPage,
    layout: "dashboard",
    protected: true,
    metadata: { title: "List Detail" },
  },
];

/**
 * Settings routes (nested under settings layout)
 */
export const settingsRoutes: RouteConfig[] = [
  {
    path: "",
    component: SettingsPage,
    layout: "settings",
    protected: true,
    metadata: { title: "Settings" },
  },
  {
    path: "ai/providers",
    component: AIProvidersSetupPage,
    layout: "settings",
    protected: true,
    metadata: { title: "AI Providers" },
  },
  {
    path: "personal",
    component: PersonalSettingsPage,
    layout: "settings",
    protected: true,
    metadata: { title: "Personal Settings" },
  },
  {
    path: "notifications",
    component: NotificationPreferencesPage,
    layout: "settings",
    protected: true,
    metadata: { title: "Notification Preferences" },
  },
  {
    path: "security",
    component: SecuritySettingsPage,
    layout: "settings",
    protected: true,
    metadata: { title: "Security" },
  },
  {
    path: "social-channels",
    component: SocialChannelsAvailabilityPage,
    layout: "settings",
    protected: true,
    metadata: { title: "Social channels" },
  },
  {
    path: "platform-admin",
    component: PlatformAdminPage,
    layout: "settings",
    protected: true,
    requiredRole: "platform_admin",
    metadata: { title: "Platform Administration" },
  },
];

/**
 * Platform Admin routes (dedicated layout with side nav)
 */
export const platformAdminRoutes: RouteConfig[] = [
  {
    path: "",
    component: PlatformAdminPage, // Use redirect page for empty path
    layout: "platform-admin",
    protected: true,
    requiredRole: "platform_admin",
    metadata: { title: "Platform Administration" },
  },
  {
    path: "health",
    component: PlatformAdminHealthPage,
    layout: "platform-admin",
    protected: true,
    requiredRole: "platform_admin",
    metadata: { title: "Platform Health" },
  },
  {
    path: "orgs",
    component: PlatformAdminOrgsPage,
    layout: "platform-admin",
    protected: true,
    requiredRole: "platform_admin",
    metadata: { title: "Organizations" },
  },
  {
    path: "orgs/:orgId",
    component: PlatformAdminOrgDetailPage,
    layout: "platform-admin",
    protected: true,
    requiredRole: "platform_admin",
    metadata: { title: "Organization Detail" },
  },
  {
    path: "users",
    component: PlatformAdminUsersPage,
    layout: "platform-admin",
    protected: true,
    requiredRole: "platform_admin",
    metadata: { title: "Users" },
  },
  {
    path: "users/:userId",
    component: PlatformAdminUserDetailPage,
    layout: "platform-admin",
    protected: true,
    requiredRole: "platform_admin",
    metadata: { title: "User Detail" },
  },
  {
    path: "plans",
    component: PlatformAdminPlansPage,
    layout: "platform-admin",
    protected: true,
    requiredRole: "platform_admin",
    metadata: { title: "Plans" },
  },
  {
    path: "audit-logs",
    component: PlatformAdminAuditLogsPage,
    layout: "platform-admin",
    protected: true,
    requiredRole: "platform_admin",
    metadata: { title: "Audit Logs" },
  },
  {
    path: "base-table",
    component: PlatformAdminBaseTablePage,
    layout: "platform-admin",
    protected: true,
    requiredRole: "platform_admin",
    metadata: { title: "BASE Table" },
  },
  {
    path: "templates",
    component: PlatformAdminTemplatesPage,
    layout: "platform-admin",
    protected: true,
    requiredRole: "platform_admin",
    metadata: { title: "Prompt Templates" },
  },
  {
    path: "settings",
    component: PlatformAdminSettingsPage,
    layout: "platform-admin",
    protected: true,
    requiredRole: "platform_admin",
    metadata: { title: "Settings" },
  },
];

/**
 * Helper to check if a path is a public route
 */
export function isPublicRoute(pathname: string): boolean {
  return publicRoutes.some(
    (route) =>
      route.path === pathname ||
      pathname.startsWith(route.path + "/") ||
      pathname.startsWith(route.path),
  );
}
